/**
 * Utility functions for data validation and transformation
 */

/**
 * Safely extracts array data from API response
 * Handles both direct arrays and wrapped {data: array} formats
 * @param {any} response - API response data
 * @param {string} key - Optional key to extract from response (default: 'data')
 * @returns {Array} - Validated array
 */
export const safeArray = (response, key = 'data') => {
  if (!response) return [];

  // Support direct arrays (e.g. Axios interceptor unwrapped `data`)
  if (Array.isArray(response)) return response;

  const data = key ? response[key] : response;

  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && Array.isArray(data[key])) return data[key];

  return [];
};

/**
 * Safely extracts object data from API response
 * @param {any} response - API response data
 * @param {string} key - Optional key to extract from response
 * @returns {Object} - Validated object
 */
export const safeObject = (response, key = null) => {
  if (!response) return {};

  const data = key ? response[key] : response;

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data;
  }

  return {};
};

/**
 * Validates if value is a non-empty array
 * @param {any} value
 * @returns {boolean}
 */
export const isNonEmptyArray = (value) => Array.isArray(value) && value.length > 0;

/**
 * Safely gets nested object property
 * @param {Object} obj - Source object
 * @param {string} path - Dot notation path (e.g., 'user.profile.name')
 * @param {any} defaultValue - Default value if path doesn't exist
 * @returns {any}
 */
export const getNestedValue = (obj, path, defaultValue = null) =>
  path.split('.').reduce((acc, part) => acc && acc[part], obj) ?? defaultValue;

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function}
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Formats currency (Kenyan Shillings)
 * @param {number} amount
 * @param {boolean} includeSymbol
 * @returns {string}
 */
export const formatCurrency = (amount, includeSymbol = true) => {
  const formatted = Number(amount || 0).toLocaleString('en-KE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return includeSymbol ? `KES ${formatted}` : formatted;
};

/**
 * Formats date to readable string
 * @param {string|Date} date
 * @param {boolean} includeTime
 * @returns {string}
 */
export const formatDate = (date, includeTime = false) => {
  if (!date) return '';

  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(includeTime && { hour: '2-digit', minute: '2-digit' }),
  };

  return new Date(date).toLocaleDateString('en-US', options);
};

/**
 * Validates phone number (Kenyan format)
 * @param {string} phone
 * @returns {boolean}
 */
export const isValidPhone = (phone) => /^(\+?254|0)[17]\d{8}$/.test(phone);

/**
 * Validates email
 * @param {string} email
 * @returns {boolean}
 */
export const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/**
 * Truncates text to specified length
 * @param {string} text
 * @param {number} maxLength
 * @returns {string}
 */
export const truncate = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Capitalizes first letter of each word
 * @param {string} text
 * @returns {string}
 */
export const capitalizeWords = (text) => {
  if (!text) return '';
  return text
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Deep clone object
 * @param {any} obj
 * @returns {any}
 */
export const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

/**
 * Removes undefined, null, and empty string values from object
 * @param {Object} obj
 * @returns {Object}
 */
export const cleanObject = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null && v !== ''));

/**
 * Groups array of objects by key
 * @param {Array} array
 * @param {string} key
 * @returns {Object}
 */
export const groupBy = (array, key) =>
  array.reduce((result, item) => {
    const group = item[key];
    return {
      ...result,
      [group]: [...(result[group] || []), item],
    };
  }, {});

/**
 * Calculates percentage
 * @param {number} value
 * @param {number} total
 * @returns {number}
 */
export const calculatePercentage = (value, total) => {
  if (!total || total === 0) return 0;
  return Math.round((value / total) * 100);
};

/**
 * Generates random ID
 * @returns {string}
 */
export const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export default {
  safeArray,
  safeObject,
  isNonEmptyArray,
  getNestedValue,
  debounce,
  formatCurrency,
  formatDate,
  isValidPhone,
  isValidEmail,
  truncate,
  capitalizeWords,
  deepClone,
  cleanObject,
  groupBy,
  calculatePercentage,
  generateId,
};
